// The workspace: who is signed in, which office, which administration.
//
// One context, one source of truth, for a reason acceptance V03 states directly:
// "A reviewer can travel from Today to a document, correction, approval,
// operation outcome and activity history without changing administration
// context accidentally." A selection that lives in a screen is a selection that
// resets when the screen unmounts, and the reviewer then approves something in
// an administration they were not looking at. So it lives here, above the
// screens, and a screen can read it but only the header can change it.
//
// The office (tenant) comes from GET /me, which is the only endpoint that
// answers across offices, and the administrations from GET /administrations
// INSIDE that office — the same two calls the API's own isolation tests use.
// Neither list is filtered here: what the API returns is what this session may
// see, and the app has no way to widen it.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, request, tenantPath } from '../api/client.ts';
import type { Administration, Me, Membership } from '../api/types.ts';
import { clearToken, loadToken, saveToken } from '../session.ts';

export const ALL_ADMINISTRATIONS = 'all' as const;

export interface Workspace {
  readonly token: string;
  readonly me: Me;
  readonly membership: Membership;
  readonly administrations: readonly Administration[];
  /** An administration id, or ALL_ADMINISTRATIONS. Never silently changed. */
  readonly administrationId: string;
  readonly selectAdministration: (id: string) => void;
  readonly selectTenant: (tenantId: string) => void;
  /**
   * End the session. `revoke: false` is for the case where the server has
   * already told us the token is no good — a 401 — and asking it to revoke one
   * it has just refused would be a second doomed request for no benefit.
   */
  readonly signOut: (options?: { readonly revoke?: boolean }) => void;
  /**
   * Replace the session with one the server just issued — after enrolling a
   * second factor, which ENDS the old session and returns a new one. The new
   * token is checked against GET /me like any other before it is used, and the
   * office being looked at is kept if the new session still holds it.
   */
  readonly adoptSession: (token: string) => Promise<void>;
  /** Re-read the administrations, e.g. after an action changed one. */
  readonly refreshAdministrations: () => Promise<void>;
}

/**
 * Exported so tests can mount a screen with a known workspace instead of
 * standing up the whole provider and its two network calls. Production code
 * uses WorkspaceProvider; nothing else should provide this directly.
 */
export const WorkspaceContext = createContext<Workspace | null>(null);

export function useWorkspace(): Workspace {
  const value = useContext(WorkspaceContext);
  if (value === null) {
    throw new Error('useWorkspace outside a WorkspaceProvider');
  }
  return value;
}

type Status =
  | { readonly kind: 'signed_out'; readonly error: string | null }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly token: string; readonly me: Me };

export interface WorkspaceProviderProps {
  readonly children: ReactNode;
  /** Sign-in screen, rendered when there is no usable session. */
  readonly renderSignIn: (props: {
    onToken: (token: string) => void;
    error: string | null;
    busy: boolean;
  }) => ReactNode;
}

export function WorkspaceProvider({ children, renderSignIn }: WorkspaceProviderProps): ReactNode {
  const [status, setStatus] = useState<Status>(() => {
    const stored = loadToken();
    return stored === null ? { kind: 'signed_out', error: null } : { kind: 'loading' };
  });
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [administrations, setAdministrations] = useState<readonly Administration[]>([]);
  const [administrationId, setAdministrationId] = useState<string>(ALL_ADMINISTRATIONS);
  const [busy, setBusy] = useState(false);

  const signIn = useCallback(async (token: string) => {
    setBusy(true);
    try {
      // The server decides whether this session is real. A token that does not
      // verify never becomes a signed-in state here.
      const me = await request<Me>('/v1/me', { token });
      saveToken(token);
      setStatus({ kind: 'ready', token, me });
      // Keep the office being looked at when a session is REPLACED (after a
      // second factor is enrolled) and the new one still holds it.
      setTenantId((current) =>
        current !== null && me.memberships.some((m) => m.tenantId === current)
          ? current
          : (me.memberships[0]?.tenantId ?? null),
      );
    } catch (error) {
      clearToken();
      setStatus({
        kind: 'signed_out',
        error:
          error instanceof ApiError && error.status === 401
            ? 'Your session has ended. Sign in again.'
            : error instanceof ApiError && error.code === 'transport_error'
              ? 'The service could not be reached. Check the API address and that this origin is allowed.'
              : 'Sign-in failed.',
      });
    } finally {
      setBusy(false);
    }
  }, []);

  // A token restored from a previous page load is verified the same way a typed
  // one is: by asking the server. There is no path that trusts storage.
  useEffect(() => {
    if (status.kind !== 'loading') return;
    const stored = loadToken();
    if (stored === null) {
      setStatus({ kind: 'signed_out', error: null });
      return;
    }
    void signIn(stored);
  }, [status.kind, signIn]);

  const signOut = useCallback(
    (options: { readonly revoke?: boolean } = {}) => {
      // Tell the server first, and do not wait for it.
      //
      // Discarding the token here used to be the whole of signing out, which
      // made it a local gesture: the token stayed valid until it expired, and
      // anyone holding a copy still held a session. POST /v1/session/revoke is
      // what makes it real.
      //
      // Deliberately not awaited, and deliberately swallowing failures. The
      // local half must happen whether or not the network agrees — a person who
      // presses "sign out" on a flaky connection must not be left signed in —
      // and the server-side half is idempotent, so the next successful request
      // from this session cannot undo it.
      const revoke = options.revoke ?? true;
      const current = status.kind === 'ready' ? status.token : null;
      if (revoke && current !== null) {
        void request<{ scope: string }>('/v1/session/revoke', {
          method: 'POST',
          token: current,
          body: { scope: 'current' },
        }).catch(() => undefined);
      }

      clearToken();
      setStatus({ kind: 'signed_out', error: null });
      setTenantId(null);
      setAdministrations([]);
      setAdministrationId(ALL_ADMINISTRATIONS);
    },
    [status],
  );

  const token = status.kind === 'ready' ? status.token : null;

  const loadAdministrations = useCallback(async () => {
    if (token === null || tenantId === null) return;
    const body = await request<{ administrations: Administration[] }>(
      tenantPath(tenantId, '/administrations'),
      { token },
    );
    setAdministrations(body.administrations);
  }, [token, tenantId]);

  useEffect(() => {
    if (token === null || tenantId === null) return;
    void loadAdministrations().catch((error: unknown) => {
      // The server has already refused this token; there is nothing left to
      // revoke and asking would only produce a second 401.
      if (error instanceof ApiError && error.status === 401) signOut({ revoke: false });
    });
  }, [token, tenantId, loadAdministrations, signOut]);

  // Changing office resets the administration selection, deliberately and
  // visibly: an administration id from office A means nothing in office B, and
  // carrying one across would be the accidental context change V03 forbids.
  const selectTenant = useCallback((next: string) => {
    setTenantId(next);
    setAdministrationId(ALL_ADMINISTRATIONS);
    setAdministrations([]);
  }, []);

  const value = useMemo<Workspace | null>(() => {
    if (status.kind !== 'ready' || tenantId === null) return null;
    const membership = status.me.memberships.find((entry) => entry.tenantId === tenantId);
    if (membership === undefined) return null;
    return {
      token: status.token,
      me: status.me,
      membership,
      administrations,
      administrationId,
      selectAdministration: setAdministrationId,
      selectTenant,
      signOut,
      adoptSession: signIn,
      refreshAdministrations: loadAdministrations,
    };
  }, [
    status,
    tenantId,
    administrations,
    administrationId,
    selectTenant,
    signOut,
    signIn,
    loadAdministrations,
  ]);

  if (status.kind === 'signed_out') {
    return renderSignIn({ onToken: (t) => void signIn(t), error: status.error, busy });
  }
  if (value === null) {
    return (
      <div className="signin">
        <div className="card">
          <div className="card-body">
            <h2>Loading your workspace…</h2>
            <p className="muted small" style={{ marginTop: 8 }}>
              {status.kind === 'ready' && status.me.memberships.length === 0
                ? 'This account is not a member of any office.'
                : 'Reading your memberships and administrations.'}
            </p>
          </div>
        </div>
      </div>
    );
  }
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

/** The administration filter for a request, or undefined for "all I may see". */
export function administrationFilter(id: string): string | undefined {
  return id === ALL_ADMINISTRATIONS ? undefined : id;
}
